import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabaseClient'
import { C } from './theme'
import { Scale, RefreshCw, AlertTriangle, Info, ChevronRight, ChevronDown } from 'lucide-react'

// O que ainda tenho de PAGAR ao fornecedor contra o que ainda tenho de RECEBER
// dos clientes, factura a factura.
//
// Não é a mesma conta do Lucro. Ali a pergunta é "esta compra deu lucro" e usa o
// custo TOTAL da factura, esteja pago ou não. Aqui a pergunta é de tesouraria:
// só conta o que ainda não saiu contra o que ainda não entrou.
//
// TUDO SEM IVA, dos dois lados. As compras são intracomunitárias com
// autoliquidação, logo já vêm sem IVA; do lado do cliente tira-se, porque esse
// IVA não é dinheiro nosso — é do Estado, e vive na aba do IVA.
//
// REGRA DE ENTRADA: só entra aqui o que tem factura de fornecedor ligada. Uma
// encomenda fechada há dois dias, ainda à espera que o fornecedor facture, não
// tem custo conhecido — entrar nestas contas era inventar um saldo.

const fmt = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—'

export default function SaldoFacturas() {
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [compras, setCompras] = useState([])
  const [links, setLinks] = useState([])
  const [encomendas, setEncomendas] = useState([])
  const [internos, setInternos] = useState(new Set())
  const [aberta, setAberta] = useState(null)

  const carregar = async () => {
    setLoading(true); setErro('')
    try {
      const [cRes, lRes, eRes, pRes] = await Promise.all([
        supabase.from('compras_fornecedor')
          .select('id, numero_fatura, numero_interno, data_fatura, estado, fase, valor_sem_iva, valor_final_sem_iva, valor_total, valor_final_total, pago_1a_prestacao, pago_2a_prestacao, pago_1a_valor, pago_2a_valor, sinal_percentagem')
          .order('data_fatura', { ascending: false }),
        supabase.from('compras_fornecedor_encomendas')
          .select('compra_id, encomenda_id, quantidade_alocada'),
        supabase.from('encomendas')
          .select('id, numero_encomenda, cliente_nome, cliente_id, total_sem_iva, total_com_iva, total_final, desconto_valor, pago_1a_prestacao, pago_2a_prestacao, pago_1a_valor, sinal_escolhido, tipo_pagamento, estado, historica, promovido_pedido_em'),
        supabase.from('perfis').select('id').eq('cliente_interno', true),
      ])
      for (const r of [cRes, lRes, eRes, pRes]) if (r.error) throw new Error(r.error.message)
      setCompras(cRes.data || [])
      setLinks(lRes.data || [])
      setEncomendas(eRes.data || [])
      setInternos(new Set((pRes.data || []).map(p => p.id)))
    } catch (e) { setErro(e.message || String(e)) }
    setLoading(false)
  }
  useEffect(() => { carregar() }, [])

  const { facturas, totais, repetidas, porLigar, comIva } = useMemo(() => {
    const encPorId = new Map(encomendas.map(e => [e.id, e]))

    // Uma encomenda tem uma linha por produto na mesma factura — para o dinheiro
    // conta uma vez só. E se por acidente aparecer em duas facturas, fica na que
    // levou mais unidades: contá-la nas duas inflacionava o "a receber", que é
    // precisamente o erro que este quadro não pode ter.
    const unsPorPar = new Map()
    for (const l of links) {
      const k = `${l.encomenda_id}|${l.compra_id}`
      unsPorPar.set(k, (unsPorPar.get(k) || 0) + Number(l.quantidade_alocada || 0))
    }
    const donoDe = new Map()
    const repetidas = []
    for (const [k, un] of unsPorPar) {
      const [encId, compraId] = k.split('|')
      const actual = donoDe.get(encId)
      if (!actual) { donoDe.set(encId, { compraId, un }); continue }
      repetidas.push(encPorId.get(encId)?.numero_encomenda || encId)
      if (un > actual.un) donoDe.set(encId, { compraId, un })
    }

    const encsPorCompra = new Map()
    for (const [encId, { compraId }] of donoDe) {
      if (!encsPorCompra.has(compraId)) encsPorCompra.set(compraId, [])
      encsPorCompra.get(compraId).push(encId)
    }

    let comIva = false
    const facturas = compras
      .filter(c => c.estado !== 'cancelado')
      .map(c => {
        const ehFinal = c.fase === 'final' && Number(c.valor_final_sem_iva) > 0
        const custo = ehFinal ? Number(c.valor_final_sem_iva) : Number(c.valor_sem_iva || 0)

        // As prestações estão gravadas sobre o total da factura. Aqui trabalha-se
        // sem IVA, por isso passa-se pela proporção — que dá exactamente o mesmo
        // nas intracomunitárias (total = sem IVA) e corrige o resto.
        const baseTot = ehFinal ? Number(c.valor_final_total || 0) : Number(c.valor_total || 0)
        if (baseTot > custo + 0.01) comIva = true
        // 1.ª ao fornecedor: o que se pagou, senão a percentagem da compra
        // (50% histórico, 20% no acordo Plus Skill).
        const pctForn = Number(c.sinal_percentagem ?? 50) || 50
        const metade1 = c.pago_1a_valor != null ? Number(c.pago_1a_valor) : baseTot * pctForn / 100
        const pagoTot = (c.pago_1a_prestacao ? metade1 : 0)
          + (c.pago_2a_prestacao ? (c.pago_2a_valor != null ? Number(c.pago_2a_valor) : baseTot - metade1) : 0)
        const ratio = baseTot > 0 ? custo / baseTot : 1
        const faltaPagar = Math.max(0, Math.round((baseTot - pagoTot) * ratio * 100) / 100)

        const ids = encsPorCompra.get(c.id) || []
        const encs = ids.map(id => encPorId.get(id)).filter(Boolean).filter(e => e.estado !== 'cancelado')

        let dosClientes = 0, faltaReceber = 0, parteInterna = 0, ivaPorEntrar = 0
        const detalhe = []
        for (const e of encs) {
          const baseSem = Number(e.total_sem_iva || 0) - Number(e.desconto_valor || 0)
          const interno = internos.has(e.cliente_id)
          // Fracção da 1.ª prestação: o valor gravado quando a encomenda foi
          // alterada depois de paga, senão o sinal que o cliente escolheu (8% no
          // acordo especial), senão metade. Faltava o sinal — sem ele, uma
          // encomenda de 8% ainda por confirmar contava como 50% cobrada.
          const totCom = Number(e.total_final ?? e.total_com_iva ?? 0)
          // Faltava o degrau do pagamento a 100%. Uma encomenda do Naypes, paga
          // por inteiro a cabeca, dava fraccao 0,5 — e com a 1.a marcada e a 2.a
          // por marcar, o quadro dizia que faltava receber METADE de dinheiro
          // que ja estava na conta. Num quadro de tesouraria isso e o pior erro
          // possivel: mostra dinheiro a entrar que nunca vai entrar.
          const frac1 = totCom > 0
            ? Math.min(1, (e.pago_1a_valor != null ? Number(e.pago_1a_valor)
                         : String(e.tipo_pagamento) === '100' ? totCom
                         : e.sinal_escolhido != null ? Number(e.sinal_escolhido)
                         : totCom / 2) / totCom)
            : 0.5
          const porEntrar = (e.pago_1a_prestacao ? 0 : frac1) + (e.pago_2a_prestacao ? 0 : (1 - frac1))
          // A conta interna nunca traz dinheiro por esta via — é lucro reinvestido
          // em mercadoria, não uma dívida por cobrar.
          const falta = interno ? 0 : Math.round(baseSem * porEntrar * 100) / 100
          // O IVA sai da diferenca real entre o total e a base tributavel, nao de
          // 23% aplicado por cima. Assim a conta interna da zero sozinha (e
          // autoliquidacao) e uma encomenda isenta tambem, sem ninguem a marcar.
          const ivaEnc = interno ? 0 : Math.max(0, Math.round((totCom - baseSem) * 100) / 100)
          const ivaFalta = interno ? 0 : Math.round(ivaEnc * porEntrar * 100) / 100
          if (interno) parteInterna += baseSem
          else { dosClientes += baseSem; ivaPorEntrar += ivaFalta }
          faltaReceber += falta
          detalhe.push({
            id: e.id, numero: e.numero_encomenda, nome: e.cliente_nome, interno,
            base: baseSem, falta, ivaFalta,
            pago: (e.pago_1a_prestacao ? 1 : 0) + (e.pago_2a_prestacao ? 1 : 0),
          })
        }

        return {
          ...c, ehFinal, custo, faltaPagar, dosClientes, parteInterna, faltaReceber,
          // O IVA que vem POR CIMA do que falta receber. O cliente paga sempre
          // com IVA; a coluna de cima e sem, para poder ser comparada com o
          // fornecedor. Este e o que entra mesmo na conta — e a diferenca ja
          // tem dono.
          ivaPorEntrar: Math.round(ivaPorEntrar * 100) / 100,
          vaiEntrar: Math.round((faltaReceber + ivaPorEntrar) * 100) / 100,
          saldo: Math.round((faltaReceber - faltaPagar) * 100) / 100,
          fechada: faltaPagar < 0.01 && faltaReceber < 0.01,
          nEncomendas: encs.length,
          detalhe: detalhe.sort((a, b) => b.falta - a.falta || String(a.numero).localeCompare(String(b.numero))),
        }
      })

    const totais = facturas.reduce((a, f) => ({
      faltaPagar: a.faltaPagar + f.faltaPagar,
      faltaReceber: a.faltaReceber + f.faltaReceber,
      interna: a.interna + f.parteInterna,
      ivaPorEntrar: a.ivaPorEntrar + f.ivaPorEntrar,
      vaiEntrar: a.vaiEntrar + f.vaiEntrar,
    }), { faltaPagar: 0, faltaReceber: 0, interna: 0, ivaPorEntrar: 0, vaiEntrar: 0 })

    // Só a contagem. O valor delas não entra em lado nenhum — sem factura do
    // fornecedor não há custo conhecido para pôr do outro lado da balança.
    const ligadas = new Set(donoDe.keys())
    const porLigar = encomendas.filter(e =>
      !e.historica && e.estado !== 'cancelado' && !ligadas.has(e.id)
      && (e.pago_1a_prestacao || e.promovido_pedido_em)
    ).length

    return { facturas, totais, repetidas: [...new Set(repetidas)], porLigar, comIva }
  }, [compras, links, encomendas, internos])

  const saldoTotal = Math.round((totais.faltaReceber - totais.faltaPagar) * 100) / 100
  const activas = facturas.filter(f => !f.fechada)

  if (loading) return <div style={{ padding: '40px 28px', textAlign: 'center', color: C.textMuted, fontSize: '13px' }}>A carregar…</div>

  return (
    <div style={{ padding: '18px 28px 28px' }}>
      {erro && (
        <div style={{ padding: '10px 14px', background: C.dangerSoft, border: `1px solid ${C.danger}55`, borderRadius: '4px', color: C.danger, fontSize: '12px', marginBottom: '12px' }}>{erro}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <Kpi label="Falta pagar ao fornecedor" valor={totais.faltaPagar} cor={C.danger} />
        <Kpi label="Falta receber dos clientes" valor={totais.faltaReceber} cor={C.success}
          nota={totais.ivaPorEntrar > 0 ? `entram ${fmt(totais.vaiEntrar)} € — ${fmt(totais.ivaPorEntrar)} € são IVA` : 'sem IVA'} />
        <Kpi label={saldoTotal >= 0 ? 'Sobra' : 'Sai do bolso'} valor={Math.abs(saldoTotal)}
          cor={saldoTotal >= 0 ? C.primary : C.warning} destaque
          nota={saldoTotal >= 0 ? 'entra mais do que falta sair' : 'falta sair mais do que entra'} />
      </div>

      {repetidas.length > 0 && (
        <div style={{ padding: '9px 12px', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: '4px', color: '#92400e', fontSize: '12px', marginBottom: '12px', lineHeight: 1.6 }}>
          <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
          <strong>{repetidas.join(', ')}</strong> {repetidas.length > 1 ? 'aparecem' : 'aparece'} em mais do que uma factura.
          Para não contar o mesmo dinheiro duas vezes, {repetidas.length > 1 ? 'ficaram' : 'ficou'} atribuíd{repetidas.length > 1 ? 'as' : 'a'} à
          factura que levou mais unidades. Vale a pena confirmar a ligação.
        </div>
      )}

      {comIva && (
        <div style={{ padding: '9px 12px', background: '#eff6fc', border: `1px solid ${C.info}55`, borderRadius: '4px', color: C.text, fontSize: '12px', marginBottom: '12px', lineHeight: 1.6 }}>
          <Info size={13} color={C.info} style={{ verticalAlign: 'middle', marginRight: 5 }} />
          Há facturas com IVA. O que falta pagar é mostrado sem IVA, proporcional, para poder ser comparado
          com o lado do cliente — do banco sai mais do que isso.
        </div>
      )}

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', minWidth: '860px' }}>
          <thead>
            <tr style={{ background: C.bg }}>
              <th style={th}>Factura</th>
              <th style={{ ...th, textAlign: 'right' }}>Custo s/IVA</th>
              <th style={{ ...th, textAlign: 'right' }}>Falta pagar</th>
              <th style={{ ...th, textAlign: 'center' }}>Enc.</th>
              <th style={{ ...th, textAlign: 'right' }}>Valor clientes</th>
              <th style={{ ...th, textAlign: 'right' }}>Falta receber</th>
              <th style={{ ...th, textAlign: 'right' }}>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {facturas.length === 0 && (
              <tr><td colSpan="7" style={{ padding: '24px', textAlign: 'center', color: C.textMuted }}>Ainda não há facturas de fornecedor.</td></tr>
            )}
            {facturas.map(f => {
              const open = aberta === f.id
              return (
                <React.Fragment key={f.id}>
                  <tr onClick={() => setAberta(open ? null : f.id)}
                    style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer', background: f.fechada ? C.bg : 'transparent', opacity: f.fechada ? 0.7 : 1 }}>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontWeight: '700', color: C.text }}>
                        {open ? <ChevronDown size={13} color={C.textMuted} /> : <ChevronRight size={13} color={C.textMuted} />}
                        {f.numero_fatura || f.numero_interno || '—'}
                      </span>
                      <span style={{ color: C.textMuted, fontSize: '11px', marginLeft: '6px' }}>{fmtData(f.data_fatura)}</span>
                      {!f.ehFinal && <span title="Ainda em previsão — o valor final com portes pode subir"
                        style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#92400e', background: '#fff4ce', border: '1px solid #c0740055', borderRadius: 8, padding: '1px 6px' }}>previsão</span>}
                      {f.fechada && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: C.success }}>fechada</span>}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: C.textMuted }}>{fmt(f.custo)} €</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: f.faltaPagar > 0 ? C.danger : C.textMuted }}>
                      {f.faltaPagar > 0 ? fmt(f.faltaPagar) + ' €' : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'center', color: C.textMuted }}>{f.nEncomendas || '—'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', color: C.textMuted }}>
                      {fmt(f.dosClientes)} €
                      {f.parteInterna > 0 && (
                        <div title="Comprado para a conta interna — lucro reinvestido, não há nada a cobrar"
                          style={{ fontSize: '10px', color: C.primary, fontWeight: '700' }}>
                          + {fmt(f.parteInterna)} € interna
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: f.faltaReceber > 0 ? C.success : C.textMuted }}>
                      {f.faltaReceber > 0 ? fmt(f.faltaReceber) + ' €' : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '800', color: f.fechada ? C.textMuted : (f.saldo >= 0 ? C.primary : C.warning) }}>
                      {f.fechada ? '—' : (f.saldo >= 0 ? '+' : '−') + fmt(Math.abs(f.saldo)) + ' €'}
                    </td>
                  </tr>
                  {open && (
                    <tr style={{ background: C.bg }}>
                      <td colSpan="7" style={{ padding: '10px 12px 12px 30px' }}>
                        {f.detalhe.length === 0 ? (
                          <span style={{ fontSize: '12px', color: C.textMuted }}>
                            Esta factura ainda não tem encomendas ligadas — por isso não tem nada a receber do outro lado.
                          </span>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <tbody>
                              {f.detalhe.map(d => (
                                <tr key={d.id} style={{ borderTop: `1px solid ${C.border}` }}>
                                  <td style={{ padding: '5px 8px', fontWeight: '600', color: C.text, width: '110px' }}>{d.numero}</td>
                                  <td style={{ padding: '5px 8px', color: C.text }}>
                                    {d.nome}
                                    {d.interno && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: C.primary, background: '#eff6fc', borderRadius: 8, padding: '1px 6px' }}>conta interna</span>}
                                  </td>
                                  <td style={{ padding: '5px 8px', textAlign: 'center', color: C.textMuted, fontSize: '11px' }}>
                                    {d.interno ? 'paga' : `${d.pago}/2 prestações`}
                                  </td>
                                  <td style={{ padding: '5px 8px', textAlign: 'right', color: C.textMuted }}>{fmt(d.base)} €</td>
                                  <td style={{ padding: '5px 8px', textAlign: 'right', width: '190px', fontWeight: '700', color: d.falta > 0 ? C.success : C.textMuted }}>
                                    {d.falta > 0 ? 'falta ' + fmt(d.falta) + ' €' : '—'}
                                    {d.ivaFalta > 0.01 && (
                                      <span style={{ fontWeight: '400', color: C.textMuted }}>
                                        {' '}+ {fmt(d.ivaFalta)} € IVA
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg }}>
              <td colSpan="2" style={{ padding: '10px 12px', fontWeight: '800', color: C.text }}>
                TOTAL
                <span style={{ fontWeight: '400', color: C.textMuted, fontSize: '11px' }}>
                  {' '}· {activas.length} factura(s) por fechar
                </span>
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', color: C.danger }}>{fmt(totais.faltaPagar)} €</td>
              <td></td>
              <td></td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', color: C.success }}>{fmt(totais.faltaReceber)} €</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', fontSize: '14px', color: saldoTotal >= 0 ? C.primary : C.warning, whiteSpace: 'nowrap' }}>
                {(saldoTotal >= 0 ? '+' : '−') + fmt(Math.abs(saldoTotal))} €
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* O que o cliente transfere mesmo. A tabela de cima e sem IVA para poder
          ser comparada com o fornecedor; mas na conta entra o valor com IVA, e a
          diferenca nao e folga — e do Estado. */}
      {totais.vaiEntrar > 0 && (
        <div style={{ marginTop: '14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ padding: '9px 12px', background: C.bg, borderBottom: `1px solid ${C.border}`, fontSize: '12px', fontWeight: '800', color: C.text }}>
            O que ainda vai entrar na conta
            <span style={{ fontWeight: '400', color: C.textMuted }}>
              {' '}— o cliente paga com IVA; a parte que passa do valor sem IVA é do Estado
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
            <thead>
              <tr style={{ background: C.surface }}>
                <th style={th}>Factura</th>
                <th style={{ ...th, textAlign: 'right' }}>Falta receber s/IVA</th>
                <th style={{ ...th, textAlign: 'right' }}>IVA por cima</th>
                <th style={{ ...th, textAlign: 'right' }}>Cliente vai pagar</th>
              </tr>
            </thead>
            <tbody>
              {facturas.filter(f => f.vaiEntrar > 0.01).map(f => (
                <tr key={f.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: '8px 12px', fontWeight: '700', color: C.text }}>
                    {f.numero_fatura || f.numero_interno || '—'}
                    <span style={{ fontWeight: '400', color: C.textMuted, fontSize: '11px', marginLeft: '6px' }}>{fmtData(f.data_fatura)}</span>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: C.success }}>{fmt(f.faltaReceber)} €</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '700', color: C.warning }}>+ {fmt(f.ivaPorEntrar)} €</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '800', color: C.text }}>{fmt(f.vaiEntrar)} €</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg }}>
                <td style={{ padding: '9px 12px', fontWeight: '800', color: C.text }}>TOTAL</td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '800', color: C.success }}>{fmt(totais.faltaReceber)} €</td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '800', color: C.warning }}>+ {fmt(totais.ivaPorEntrar)} €</td>
                <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '800', fontSize: '14px', color: C.text }}>{fmt(totais.vaiEntrar)} €</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <p style={{ margin: 0, fontSize: '11.5px', color: C.textMuted, lineHeight: 1.65, maxWidth: '720px' }}>
          Tudo <strong style={{ color: C.text }}>sem IVA dos dois lados</strong>. As compras são intracomunitárias com
          autoliquidação e já vêm sem IVA; do lado do cliente tira-se, porque esses 23% são do Estado e vivem na aba do IVA.
          <br />
          <strong style={{ color: C.text }}>Só entra o que tem factura ligada.</strong> Uma encomenda fechada à espera
          que o fornecedor facture ainda não tem custo conhecido — contá-la aqui era inventar um saldo.
          {porLigar > 0 && (
            <> Há <strong style={{ color: C.text }}>{porLigar} encomenda(s)</strong> nessa situação; não entram em nenhum número acima.</>
          )}
          <br />
          <strong style={{ color: C.text }}>A conta interna não tem nada a receber</strong> — é lucro reinvestido em
          mercadoria, não uma dívida. Aparece à parte para o valor dos clientes não parecer menor do que devia.
          <br />
          <strong style={{ color: C.text }}>O IVA do quadro de baixo não é o IVA que tens a entregar</strong> — é só o
          que vem em cima destas encomendas. O valor a entregar está na aba do IVA, sai do Sage e inclui os serviços
          e tudo o que facturas fora da plataforma.
        </p>
        <button onClick={carregar}
          style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
          <RefreshCw size={12} /> Actualizar
        </button>
      </div>
    </div>
  )
}

function Kpi({ label, valor, cor, destaque, nota }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${destaque ? cor + '66' : C.border}`, borderRadius: '4px', padding: '12px 14px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: '800', color: cor, marginTop: '3px' }}>{fmt(valor)} €</div>
      {nota && <div style={{ fontSize: '10.5px', color: C.textMuted, marginTop: '1px' }}>{nota}</div>}
    </div>
  )
}

const th = { padding: '8px 12px', textAlign: 'left', fontWeight: '700', color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.4px' }
